import World from '@/commons/world/World';
import ModelLoader from '@/commons/assetsManager/ModelLoader';
import LightHelper from '@/commons/lights/LightHelper';
import environmentController from '@/commons/environment/EnvironmentController';
import RenderPresets from '@/commons/renderPresetToggleList/RenderPreset';
import applyRenderPreset from '@/commons/renderPresetToggleList/applyRenderPreset';
import ArcRotateCamera from '@/commons/camera/ArcRotateCamera';
import Events from '@/services/Events';
import {
    Color,
    Mesh,
    CircleGeometry,
    Object3D,
    Vector3,
    ShadowMaterial,
    PCFShadowMap,
    AnimationMixer,
    MeshPhysicalMaterial,
    type MeshPhysicalMaterialParameters,
    MeshStandardMaterial,
    SRGBColorSpace,
    AnimationAction,
    LoopPingPong,
} from 'three';
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js';
import type { TCameraParams } from '@/commons/camera/TCameraParams';
import type { TWorldParams } from '@/commons/world/TWorldParams';
import type { TLoopInfo } from '@/commons/mainLoop/TLoopInfo';
import type CustomOrbitControls from '@/commons/camera/controls/CustomOrbitControls';
import type { IProgressElement } from '@/commons/assetsManager/IProgressElement';

class GuidettiWorld extends World {
    private static _instance: GuidettiWorld;

    initialRotationY: number;
    helmet: Object3D = new Object3D();
    _camera: ArcRotateCamera;
    customMaterial: MeshStandardMaterial;
    isZoomed = false;
    animMixer?: AnimationMixer;
    animAction?: AnimationAction;
    animTiming: number | undefined = undefined;
    isLoaded: Promise<void>;
    resolvePromise: () => void;
    get camera(): ArcRotateCamera {
        return this._camera;
    }

    static get pole() {
        return GuidettiWorld.instance?.helmet || new Object3D();
    }

    static get scene() {
        return GuidettiWorld.instance?.scene;
    }

    static get renderer() {
        return GuidettiWorld.instance?.renderer;
    }

    static get environmentMap() {
        return environmentController.getEnvironmentMap;
    }

    static animateCamera(isAnimated: boolean) {
        if (!GuidettiWorld.instance) return;
        (GuidettiWorld.instance.camera.controls as CustomOrbitControls).autoRotate = isAnimated;
    }

    constructor(params: TWorldParams) {
        super(params);
        this.isLoaded = new Promise<void>((resolve) => {
            this.resolvePromise = resolve;
        });
        GuidettiWorld._instance = this;

        this._renderer.shadowMap.enabled = true;
        this._renderer.shadowMap.type = PCFShadowMap;
        this._renderer.shadowMap.autoUpdate = true;
        this._renderer.shadowMap.needsUpdate = true;

        this._scene.background = new Color('#dac4c7');
        const cameraParams: TCameraParams = {
            aspect: window.innerWidth / window.innerHeight,
            near: 0.1, // augment this value to improve performance
            far: 7, // reduce this value to improve performance
            fov: 40,
            name: 'main-camera',
            rendererDomElement: this.renderer.domElement,
        };
        this._mainLoop.addCallback(() => {
            this._camera.controls.update();
            this._camera.render();
            this._camera.camAnimation?.update();
            // @ts-ignore
            this.animMixer?.update(this.animTiming);
        });
        RectAreaLightUniformsLib.init();

        this._camera = new ArcRotateCamera(cameraParams);

        environmentController.initialize(this.scene, this.renderer);

        this.customMaterial = params.customMaterial || new MeshStandardMaterial();
        this.importGuidettiModel().then(() => {
            this.resolvePromise();
        });

        this.configureCamera();
        LightHelper.init();

        this._mainLoop.start();
        this.resize();
        applyRenderPreset(RenderPresets.default);
        this.createGround();
        window.addEventListener('wheel', this.wheelEvent);
    }

    createGround() {
        // adding ground
        const groundGeometry = new CircleGeometry(1, 24);
        const material = new ShadowMaterial();
        const groundCircle = new Mesh(groundGeometry, material);
        groundCircle.name = 'ground';
        groundCircle.castShadow = true;
        groundCircle.rotation.x = -Math.PI / 2;
        groundCircle.position.add(new Vector3(0, -1, 0));
        groundCircle.receiveShadow = true;
        groundCircle.position.y = 0.415;
        groundCircle.material.opacity = 0.35;
        this.scene.add(groundCircle);
    }

    configureCamera() {
        const camHeight = 0.71;

        this.camera.position.x = 0.75;
        this.camera.position.y = camHeight;
        this._camera.controls.enableDamping = true;
        this._camera.controls.dampingFactor = 0.15;
        this.camera.controls.target.set(0, camHeight, 0);
        this._camera.controls.minDistance = 0.145;
        this.camera.controls.maxDistance = 0.95;
        this._camera.controls.rotateSpeed = 1.5;
        (this._camera.controls as CustomOrbitControls).autoRotateSpeed = -4.5;
        const { maxPolarAngle } = this._camera.controls as CustomOrbitControls;
        (this._camera.controls as CustomOrbitControls).minPolarAngle = maxPolarAngle / 2;
        (this._camera.controls as CustomOrbitControls).maxPolarAngle = maxPolarAngle / 2;

        this._camera.controls.enablePan = false;
        this.camera.setRadius(0.85);
    }

    static closePollAnimation() {
        if (!this._instance.animAction) return;
        if (!this._instance.animAction.paused) {
            this._instance.animAction.timeScale *= -1;
        } else {
            this._instance.animAction.paused = false;
        }
        this._instance.animAction.paused = false;
        // this._instance.animAction.timeScale *= -1;
        this._instance.camera.pollOpen = false;
        this.instance?.camera?.animCameraToNewPosition({
            target: new Vector3(0, 0.71, 0),
            radius: 0.85,
        });
    }

    static openPollAnimation() {
        if (!this._instance.animAction) return;
        if (!this._instance.animAction.paused) {
            this._instance.animAction.timeScale *= -1;
        } else {
            this._instance.animAction.paused = false;
        }
        this._instance.camera.pollOpen = true;
        this.instance?.camera?.animCameraToNewPosition({
            target: new Vector3(0, 0.375, 0),
            radius: 2,
        });
    }

    importGuidettiModel() {
        Events.emit('loading-start', 'Pole');
        const progress: IProgressElement = {
            name: 'Pole',
            progress: 0,
        };
        this.scene?.addEventListener('added', () => {
            Events.emit('loading-start', 'Pole');
            const poleOnScene = this.scene.getObjectByName('Pole');
            if (progress.progress < 100) {
                progress.progress += 10;
                Events.emit('loading-progress', progress);
            }
            if (!poleOnScene) return;
            Events.emit('loading-end', 'Pole');
        });
        return Promise.allSettled([ModelLoader.loadModel('/models/Guidetti_Stick.glb')]).then((modelPromise) => {
            modelPromise.forEach((promise) => {
                if (promise.status !== 'fulfilled') {
                    return;
                }

                promise.value.promise.then(() => {
                    const { model } = promise.value;
                    // const stickFolder = WorldDebugPanel.meshesFolder.addFolder({ title: 'Guidetti Stick' });
                    // @ts-ignore
                    this.animMixer = new AnimationMixer(model.scene);
                    this.animMixer.timeScale = -1;
                    this.animAction = this.animMixer.clipAction(model.animations[0]);
                    this.animAction.setLoop(LoopPingPong, Infinity);
                    this.animTiming = 1000;
                    this.animAction.play();
                    this.animMixer.addEventListener('loop', () => {
                        this.animTiming = 0.025;
                        this.animAction!.paused = true;
                    });
                    // @ts-ignore
                    model.scene.traverse((child) => {
                        if (!child?.material) {
                            return;
                        }
                        const materialCopy = child.material.clone();
                        child.frustumCulled = false;
                        child.material = materialCopy;
                        child.material.envMapIntensity = 1;
                        if (child.material.map) {
                            child.material.map.colorSpace = SRGBColorSpace;
                        }
                        if (child.name === 'Guidetti_Stick_1') {
                            child.name = 'Metal Stick';
                        }
                        if (child.name === 'Guidetti_Stick_2') {
                            child.name = 'Configurable Part';
                            child.material = this.customMaterial;
                        }
                        if (child.name === 'Guidetti_Stick001') {
                            child.name = 'Dragonne';
                            child.material.roughness = 0.85;
                            child.material.color = new Color(96 / 255, 91 / 255, 91 / 255); // {96, g: 91, b: 91}
                        }
                        if (child.name === 'Guidetti_Stick002') {
                            child.name = 'Poignée Liège';
                            child.material.metalnessMap = null;
                            child.material.metalness = 0;
                            child.material.normalScale.x = 3;
                            child.material.normalScale.y = 3;
                            child.material.color = new Color(136 / 255, 92 / 255, 92 / 255); // {r: 131, g: 87, b: 87}
                            child.material.roughness = 0.9;
                        }
                        if (child.name === 'Guidetti_Stick003') {
                            child.name = 'Embout Plastique';
                            child.material.roughness = 1.75;
                        }
                        if (child.name === 'Guidetti_Stick004') {
                            child.name = 'Plastique transparent';
                            const { map, normalMap, roughnessMap } = child.material;
                            const meshPhysicalMatParam: MeshPhysicalMaterialParameters = {
                                color: 0xffffff,
                                attenuationColor: 0x4080e7,
                                specularColor: 0x4080e7,
                                metalness: 0,
                                roughness: 1.75,
                                envMapIntensity: 1,
                                opacity: 1,
                                side: 2,
                                thickness: 3,
                                ior: 1.2,
                                transmission: 0.7,
                            };
                            const newPhysicalMat = new MeshPhysicalMaterial(meshPhysicalMatParam);
                            newPhysicalMat.map = map;
                            newPhysicalMat.normalMap = normalMap;
                            newPhysicalMat.roughnessMap = roughnessMap;
                            child.material = newPhysicalMat;
                        }
                        if (child.name === 'Guidetti_Stick006') {
                            child.name = 'Metal maintien';
                            child.material.roughness = 0.45;
                            child.material.metalness = 0.8;
                            const middleGrey = 89 / 255;
                            child.material.color = new Color(middleGrey, middleGrey, middleGrey);
                        }
                        if (child.name === 'Guidetti_Stick005') {
                            child.name = 'Embout Carbure';
                        }
                        child.castShadow = true;
                        environmentController.applyEnvmapToMesh(child as Mesh);
                        // WorldDebugPanel.addObject3DToControlPanel(child, (stickFolder || new Pane()) as FolderApi);
                    });
                    // @ts-ignore
                    model.scene.position.y = -0.3;
                    // @ts-ignore
                    model.scene.name = 'Pole';
                    // @ts-ignore
                    this.scene.add(model.scene);
                });
            });
        });
    }

    static zoomOnPole() {
        const objectToZoomIn = ['Poignée Liège', 'Embout Plastique'].map(
            (name) => GuidettiWorld.instance?.scene.getObjectByName(name) ?? new Object3D(),
        );
        GuidettiWorld.instance?.camera.zoomOnSelection(objectToZoomIn, 1);
    }

    static zoomOnConfigPart(offset = 0) {
        const configurePart = GuidettiWorld.instance?.scene.getObjectByName('Configurable Part');
        if (!configurePart) return;
        GuidettiWorld.instance?.camera.zoomOnSelection([configurePart], 0.45 + offset);
    }

    render(loopInfo?: TLoopInfo) {
        super.render(loopInfo);
    }

    dispose(): void {
        this._camera.dispose();
        window.removeEventListener('wheel', this.wheelEvent);
        super.dispose();
    }

    wheelEvent = () => {
        const limitRadius = 0.6;
        if (!this.isZoomed && this._camera.radius < limitRadius) {
            this.isZoomed = true;
            Events.emit('zoomed', `${!this.isZoomed}`);
        }
        if (this.isZoomed && this._camera.radius > limitRadius) {
            this.isZoomed = false;
            Events.emit('zoomed', `${!this.isZoomed}`);
        }
    };

    public static get instance(): GuidettiWorld | undefined {
        if (!GuidettiWorld._instance) return undefined;

        return GuidettiWorld._instance;
    }
}

export { GuidettiWorld };
